import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { X, Send, Sparkles, Loader2 } from 'lucide-react';
import { useGamification } from '../context/GamificationContext';

const API = `${''}/api`;
const CAT_LABELS = { bug: 'Bug', erreur: 'Erreur médicale', suggestion: 'Suggestion', source: 'Source', autre: 'Retour' };

export const ImproveChatModal = ({ open, onClose }) => {
  const { notifyResult } = useGamification();
  const [messages, setMessages] = useState([{
    role: 'bot',
    text: "Bonjour Docteur 👋 Je suis l'Assistant Progrès. Signalez-moi une erreur, un bug, ou proposez une amélioration ou une source fiable : chaque retour vous fait progresser et améliore Hippocrate MG.",
  }]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, open]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setMessages((m) => [...m, { role: 'user', text: msg }]);
    setInput('');
    setBusy(true);
    try {
      const res = await axios.post(`${API}/feedback/chat`, { message: msg, context: window.location.pathname });
      setMessages((m) => [...m, { role: 'bot', text: res.data.reply, category: res.data.category, awarded: res.data.awarded }]);
      notifyResult?.(res.data);
    } catch (e) {
      setMessages((m) => [...m, { role: 'bot', text: 'Désolé, une erreur est survenue. Merci de réessayer.' }]);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="improve-modal"
    >
      <div
        className="w-full sm:max-w-lg bg-slate-900 border border-slate-700 rounded-t-2xl sm:rounded-2xl flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">Assistant Progrès</h3>
              <p className="text-[11px] text-slate-400">Aidez-nous à améliorer l'outil</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="improve-close" className="p-2 text-slate-400 hover:text-white" aria-label="Fermer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.role === 'user' ? 'bg-cyan-600 text-white' : 'bg-slate-800 text-slate-100'}`}>
                {m.category && (
                  <span data-testid="improve-category" className="block mb-1 text-[10px] uppercase tracking-wide text-amber-300">
                    {CAT_LABELS[m.category] || 'Retour'}{m.awarded ? ` · +${m.awarded} pts` : ''}
                  </span>
                )}
                <p className="whitespace-pre-wrap">{m.text}</p>
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="bg-slate-800 rounded-2xl px-3.5 py-2.5"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="p-3 border-t border-slate-800 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder="Votre retour…"
            className="flex-1 px-3 py-2.5 min-h-[44px] bg-slate-950/50 border border-slate-800 rounded-xl text-slate-200 text-sm"
            data-testid="improve-input"
          />
          <button onClick={send} disabled={busy} data-testid="improve-send" className="px-4 rounded-xl bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-50" aria-label="Envoyer">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
